import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  useGetAiTemplatesQuery,
  useDeleteAiTemplateMutation,
  useArchiveAiTemplateMutation,
  useRestoreAiTemplateMutation,
} from "@/store/admin_api";
import {
  AI_AGENT_CATEGORIES,
  AI_TEMPLATE_STATUS_FILTERS,
  AI_VOICE_ORCHESTRATORS,
  AiAgentTemplate,
  ARCHIVED_TEMPLATE_STATUS,
  templateStatusLabel,
} from "@/model/interface/AiAgent";
import { apiErrorMessage } from "@/util/apiError";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function AiTemplatesAdmin() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("ACTIVE");
  const [page, setPage] = useState(0);
  const { data, isLoading } = useGetAiTemplatesQuery({
    status: statusFilter === "ACTIVE" ? undefined : statusFilter,
    page,
    size: 25,
  });
  const [deleteTemplate] = useDeleteAiTemplateMutation();
  const [archiveTemplate] = useArchiveAiTemplateMutation();
  const [restoreTemplate] = useRestoreAiTemplateMutation();
  const [archiveTarget, setArchiveTarget] = useState<AiAgentTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AiAgentTemplate | null>(null);

  const templates = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;

  const changeFilter = (nextFilter: string) => {
    setStatusFilter(nextFilter);
    setPage(0);
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await deleteTemplate(deleteTarget.id).unwrap();
      toast.success("Template deleted");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to delete"));
    }
    setDeleteTarget(null);
  };

  const handleArchive = async () => {
    if (!archiveTarget) {
      return;
    }
    try {
      await archiveTemplate(archiveTarget.id).unwrap();
      toast.success("Template archived");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to archive"));
    }
    setArchiveTarget(null);
  };

  const handleRestore = async (template: AiAgentTemplate) => {
    try {
      await restoreTemplate(template.id).unwrap();
      toast.success("Template restored as a draft");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to restore"));
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED": return "default";
      case "DRAFT": return "secondary";
      case ARCHIVED_TEMPLATE_STATUS: return "outline";
      default: return "secondary";
    }
  };

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Agent Templates</h3>
          <p className="text-sm text-muted-foreground">Define AI agent configurations that can be published to the marketplace.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={changeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AI_TEMPLATE_STATUS_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>{filter.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => navigate("/platform-admin/ai-agents/templates/new")}>New Template</Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Orchestrator</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Version</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((template) => {
            const status = template.status ?? "DRAFT";
            const isArchived = status === ARCHIVED_TEMPLATE_STATUS;
            return (
              <TableRow
                key={template.id}
                className="cursor-pointer"
                onClick={() => navigate(`/platform-admin/ai-agents/templates/${template.id}`)}
              >
                <TableCell>
                  <div className="font-medium">{template.name}</div>
                  {template.description && <div className="text-xs text-muted-foreground truncate max-w-xs">{template.description}</div>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{AI_AGENT_CATEGORIES.find((cat) => cat.value === template.category)?.label || template.category}</Badge>
                </TableCell>
                <TableCell>
                  {AI_VOICE_ORCHESTRATORS.find((entry) => entry.value === template.orchestrator)?.label ||
                    template.orchestrator}
                </TableCell>
                <TableCell>
                  <Badge variant={statusColor(status) as "default" | "secondary" | "outline"}>{templateStatusLabel(status)}</Badge>
                </TableCell>
                <TableCell>v{template.version}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {status === "DRAFT" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/platform-admin/ai-agents/new/${template.id}`);
                      }}
                    >
                      Continue setup
                    </Button>
                  )}
                  {isArchived ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRestore(template);
                      }}
                    >
                      Restore
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        setArchiveTarget(template);
                      }}
                    >
                      Archive
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDeleteTarget(template);
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {templates.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground">No templates</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={data?.last ?? true} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={archiveTarget !== null} onOpenChange={() => setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive template</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">{archiveTarget?.name}</span> will be hidden from the template
              pickers, and its marketplace listing will be removed from the store so no organisation can
              acquire it. Agents already running on this template keep working. You can restore it later,
              as a draft.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">{deleteTarget?.name}</span> will be deleted permanently. Archive
              it instead if any agent was ever created from it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
